// --- Core ---
import {parseISO, setHours, setMinutes, setMonth} from "date-fns";
import {FieldArrayRenderProps} from "react-final-form-arrays";
import {FormattedMessage, useIntl} from "react-intl";
import {compact, each, map, uniq} from "underscore";
// --- Material UI ---
import {List, ListItem} from "@mui/material";
// --- SaniQ ---
import {useDayNames} from "helpers/calendar/TranslationHelper";
import {AddListItem} from "helpers/forms/AddListItem";
import CronEntry from "./cron/CronEntry";

type CronList = {
  checked: boolean;
}[];

interface Cron {
  period: "day" | "week" | "month" | "year";
  time: Date;
  // Selected days of the month. Comma separated string without any whitespace
  monthDay: string;
  // Selected months for the expression. Index 0 == January
  months: CronList;
  // Selected days of the week for the expression. Index 0 == Monday
  weekdays: CronList;
}

const defaultCron: Readonly<Cron> = Object.freeze({
  period: 'day',
  time: parseISO("2020-01-01T08:15:00"),
  monthDay: '1',
  months: parseCronList("*", 12),
  weekdays: parseCronList("*", 7)
});

function parseCronList(cronListExpr: string, maxCount: number): CronList {
  const sanitizedExpression = cronListExpr.trim();

  if (sanitizedExpression === "*") {
    return new Array(maxCount).fill({checked: true});
  }
  const entries = uniq(sanitizedExpression.split(','));
  const result: CronList = new Array(maxCount).fill({checked: false});

  each(entries, entry => {
    result[parseInt(entry) - 1] = {checked: true};
  });

  return result;
}

/* Hinweis: Nur Cron-Jobs auf täglicher Ebene werden unterstützt.
 Cron-Jobs mit minutengenauer oder stündlicher Ausführung werden ignoriert
 und führen zu einer Standard-Cron-Rückgabe. */
function parseCron(expression: string, defaultCron: Cron): Cron {
  const cronCombinations = {
    "day": /^(\d{1,2}\s){2}(\*\s){2}\*$/,                                              // "? ? * * *"
    "week": /^(\d{1,2}\s){2}(\*\s){2}(\d(,\d){0,6})$/,                                 // "? ? * * ?"
    "month": /^(\d{1,2}\s){2}(\d{1,2}(,\d{1,2}){0,30}\s)\*\s\*$/,                      // "? ? ? * *"
    "year": /^(\d{1,2}\s){2}(\d{1,2}(,\d{1,2}){0,30}\s)(\d{1,2}(,\d{1,2}){0,11}\s)\*$/ // "? ? ? ? *"
  };

  const cronElements = (expression || "").split(" ");
  const defaultTime = parseISO("2020-01-01T08:15:00");

  if (cronElements.length === 5) {
    for (const t in cronCombinations) {
      if (cronCombinations[t].test(expression)) {
        const min = parseInt(cronElements[0]);
        const hour = parseInt(cronElements[1]);
        const newTime = setMinutes(setHours(defaultTime, hour), min);

        return {
          period: t,
          time: newTime,
          monthDay: cronElements[2] === '*' ? '' : cronElements[2].replace(/,/g, ', '),
          months: parseCronList(cronElements[3], 12),
          weekdays: parseCronList(cronElements[4], 7)
        } as Cron;
      }
    }
  }

  return defaultCron;
}

function parseMultilineCron(cron: string, defaultCron: Cron): Cron[] {
  if (cron) {
    return map(cron.split(/[\r\n]+/g), cronExpr => parseCron(cronExpr, defaultCron));
  }

  return [];
}

function selectedIndices(array: CronList): number[] {
  return compact(map(array, (data, index) => (data.checked ? index + 1 : null)));
}

function formatCron(cron: Cron): string {
  let day = "*", month = "*", dow = "*";
  const selectedTime = new Date(cron.time);
  const min = selectedTime.getMinutes();
  const hour = selectedTime.getHours();
  const sanitizedDays = cron.monthDay.replace(/\s/g, '');

  switch (cron.period) {
    case "day":
      break;
    case "week": {
      const selectedDays = selectedIndices(cron.weekdays);

      if (selectedDays.length !== 7) {
        dow = selectedDays.join(",");
      }
      break;
    }
    case "month":
      day = sanitizedDays;
      break;
    case "year": {
      day = sanitizedDays;
      const selectedMonths = selectedIndices(cron.months);

      if (selectedMonths.length !== 12) {
        month = selectedMonths.join(",");
      }
      break;
    }
    default:
      break;
  }

  return [min, hour, day, month, dow].join(" ");
}

function formatMultilineCron(cronList: Cron[]): string {
  const cron = map(cronList, cronData => formatCron(cronData));

  return cron.join("\n");
}

interface CronDescriptionProps {
  cron: Cron;
}

const CronDescription = ({cron}: CronDescriptionProps) => {
  const dayNames = useDayNames('long');
  const intl = useIntl();
  let period = "";

  switch (cron.period) {
    case 'day':
      period = intl.formatMessage({
        id: "cron.description.daily",
        defaultMessage: "Daily"
      });
      break;
    case 'week': {
      const selectedWeekdays = map(selectedIndices(cron.weekdays), day => dayNames[day]);

      period = intl.formatMessage({
        id: "cron.description.weekly",
        defaultMessage: "Weekly on {days}"
      }, {
        days: selectedWeekdays.join(", ")
      });
      break;
    }
    case 'month':
      period = intl.formatMessage({
        id: "cron.description.monthly",
        defaultMessage: "Monthly on day {days}"
      }, {
        days: cron.monthDay
      });
      break;
    case 'year': {
      const selectedMonths = map(selectedIndices(cron.months), month => intl.formatDate(setMonth(new Date(), month - 1), {month: "long"}));

      period = intl.formatMessage({
        id: "cron.description.yearly",
        defaultMessage: "Yearly in {months} on day {days}"
      }, {
        days: cron.monthDay,
        months: selectedMonths.join(", ")
      });
      break;
    }
  }

  return (
    <FormattedMessage
      id="cron.description.time"
      defaultMessage="{period} at {time, time, short}"
      values={{period, time: cron.time}}
    />
  );
};

interface CronFieldProps extends FieldArrayRenderProps<Cron, HTMLElement> {
  defaultItem: Cron;
  disableDaily?: boolean;
  disabled: boolean;
  singleItem: boolean;
}

const CronField = ({disabled, fields, disableDaily, defaultItem, singleItem}: CronFieldProps) => (
  <List>
    {
      fields.map((entry, index) => (
        <ListItem disablePadding={singleItem} key={`listItem_${index}`} divider={!singleItem}>
          <CronEntry
            name={entry}
            disableDaily={disableDaily}
            period={fields.value[index].period}
            onDelete={
              singleItem ? null :
                () => {
                  fields.remove(index);
                }
            }
          />
        </ListItem>
      ))
    }
    {
      !singleItem && <AddListItem
        fields={fields} text={<FormattedMessage id="field.cron.add" defaultMessage="Add regular appointment"/>}
        disabled={disabled}
        defaultItem={defaultItem}
      />
    }

  </List>
);

export {
  Cron,
  defaultCron,
  parseCronList,
  parseCron,
  parseMultilineCron,
  formatCron,
  formatMultilineCron,
  CronField,
  CronDescription
};
